/**
 * ITER308 · Phase B.2 — Manager-shell Maintenance Work Queue.
 *
 * A single, outlet-scoped queue that lists:
 *   • Open PM tasks (pending / in_progress / awaiting verification)
 *   • Open maintenance tickets (department = Maintenance)
 *
 * Roles that reach this page (via <ManagerMaintenance /> tab):
 *   • maintenance  — can Start + Complete (with inspection results,
 *                    remarks, photos). Cannot Verify.
 *   • manager / supervisor / store_manager / maintenance_manager —
 *                    additionally see and can trigger [Verify].
 *
 * Backend contract:
 *   GET  /api/admin/maintenance/tasks         (outlet-auto-scoped)
 *   POST /api/admin/maintenance/tasks/:id/start
 *   POST /api/admin/maintenance/tasks/:id/complete
 *   POST /api/admin/maintenance/tasks/:id/verify   (verifier roles)
 *   GET  /api/admin/maintenance/inspection-templates?category_id=...
 *   POST /api/admin/maintenance/assets/:id/inspections
 *   GET  /api/tickets?department=Maintenance&status=open,in_progress
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Wrench, ClipboardList, Ticket as TicketIcon, PlayCircle,
  CheckCircle2, ShieldCheck, ArrowRight,
} from 'lucide-react';

const VERIFIER_ROLES = new Set([
  'admin', 'area_manager', 'super_admin',
  'manager', 'supervisor', 'store_manager',
  'maintenance_manager',
]);

const STATUS_STYLES = {
  pending:     { label: 'Pending',      cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  in_progress: { label: 'In progress',  cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  completed:   { label: 'Awaiting verification', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  verified:    { label: 'Verified',     cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

function HeadingBand({ title, action }) {
  return (
    <div className="rounded-lg mb-3 overflow-hidden border border-[color:var(--color-primary)]/30">
      <div className="flex items-center justify-between px-4 py-2 gap-3"
           style={{ background: 'var(--color-primary)', color: 'white' }}>
        <div className="text-sm font-semibold tracking-wide">{title}</div>
        {action}
      </div>
    </div>
  );
}

function relativeDue(dueDate) {
  if (!dueDate) return '—';
  const d = new Date(dueDate);
  const now = new Date();
  const days = Math.round((d - now) / 86400000);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'due today';
  return `in ${days}d`;
}

export default function ManagerMaintenanceQueue() {
  const { user, getHeaders, API } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets] = useState({});      // id → asset
  const [templates, setTemplates] = useState({}); // id → template
  const [loading, setLoading] = useState(true);
  const [openTask, setOpenTask] = useState(null);

  const isVerifier = VERIFIER_ROLES.has(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tasks: exclude verified (they're done). Backend auto-scopes to outlet.
      const [tRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/tasks?status=pending,in_progress,completed`,
                  { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/assets`, { headers: getHeaders() }),
      ]);
      setTasks(tRes.data);
      const aMap = {};
      for (const a of aRes.data) aMap[a.id] = a;
      setAssets(aMap);

      // Templates: fetch once, keyed by id
      const tplRes = await axios.get(`${API}/admin/maintenance/inspection-templates`,
                                      { headers: getHeaders() });
      const tplMap = {};
      for (const t of tplRes.data) tplMap[t.id] = t;
      setTemplates(tplMap);

      // Tickets: maintenance dept, still open
      try {
        const tkRes = await axios.get(`${API}/tickets?department=Maintenance&status=open,in_progress`,
                                       { headers: getHeaders() });
        setTickets(tkRes.data);
      } catch { setTickets([]); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load queue');
    } finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byStatus = { pending: [], in_progress: [], completed: [] };
    for (const t of tasks) {
      if (byStatus[t.status]) byStatus[t.status].push(t);
    }
    return byStatus;
  }, [tasks]);

  return (
    <div data-testid="manager-maintenance-queue">
      {loading && <div className="text-sm text-[#5C6B64] py-4">Loading queue…</div>}

      <HeadingBand
        title={`My Work · ${tasks.length} open PM task${tasks.length === 1 ? '' : 's'}`}
      />
      {tasks.length === 0 ? (
        <Card className="border-dashed border-[#E2E8DF] shadow-none mb-4">
          <CardContent className="py-10 text-center">
            <ClipboardList className="w-8 h-8 text-[#5C6B64] mx-auto mb-2 opacity-60" />
            <div className="text-sm font-medium text-[#1A2421]">All caught up</div>
            <div className="text-xs text-[#5C6B64] mt-1">
              No open preventive-maintenance tasks for this outlet.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 mb-6">
          {['pending', 'in_progress', 'completed'].map((s) => {
            const rows = grouped[s];
            if (!rows || rows.length === 0) return null;
            const label = STATUS_STYLES[s].label;
            return (
              <div key={s} data-testid={`queue-section-${s}`}>
                <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-2">
                  {label} · {rows.length}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {rows.map((t) => (
                    <TaskRow key={t.id} task={t}
                             asset={assets[t.asset_id]}
                             template={templates[t.template_id]}
                             onOpen={() => setOpenTask(t)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <HeadingBand
        title={`Open Tickets · ${tickets.length}`}
        action={
          <a href="/manager/tickets"
             className="text-xs text-white/90 hover:text-white inline-flex items-center gap-1"
             data-testid="queue-tickets-link">
            Full board <ArrowRight className="w-3 h-3" />
          </a>
        }
      />
      {tickets.length === 0 ? (
        <Card className="border-dashed border-[#E2E8DF] shadow-none">
          <CardContent className="py-8 text-center">
            <TicketIcon className="w-7 h-7 text-[#5C6B64] mx-auto mb-2 opacity-60" />
            <div className="text-xs text-[#5C6B64]">No open maintenance tickets.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.slice(0, 8).map((t) => (
            <Card key={t.id} className="border-[#E2E8DF] shadow-sm"
                  data-testid={`queue-ticket-${t.id}`}>
              <CardContent className="py-2.5 px-4 flex items-center gap-3">
                <span className="font-mono text-[11px] text-[color:var(--color-primary)] font-bold">
                  {t.ticket_id || String(t.id).slice(0, 8)}
                </span>
                <span className="text-sm text-[#1A2421] flex-1 truncate">
                  {t.task_name || t.description || 'Maintenance ticket'}
                </span>
                <Badge className={`text-[10px] border ${
                  t.status === 'in_progress'
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : 'bg-slate-100 text-slate-700 border-slate-300'
                }`}>
                  {t.status}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TaskExecutionDialog
        task={openTask}
        asset={openTask ? assets[openTask.asset_id] : null}
        template={openTask ? templates[openTask.template_id] : null}
        isVerifier={isVerifier}
        onClose={() => setOpenTask(null)}
        onSaved={() => { setOpenTask(null); load(); }}
      />
    </div>
  );
}

function TaskRow({ task, asset, template, onOpen }) {
  const status = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left w-full"
      data-testid={`task-row-${task.id}`}
    >
      <Card className="border-[#E2E8DF] shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="py-3 px-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-[color:var(--color-primary)] font-bold tracking-wider">
                  {asset?.asset_tag || '—'}
                </span>
                <Badge className={`text-[10px] px-1.5 py-0.5 border ${status.cls}`}>
                  {status.label}
                </Badge>
                {template && (
                  <span className="text-[10px] text-[#5C6B64] inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> {template.name}
                  </span>
                )}
              </div>
              <div className="text-sm text-[#1A2421] mt-1 truncate">
                {asset?.name || 'Asset'}
              </div>
              <div className="text-[11px] text-[#5C6B64] mt-0.5">
                {task.description} · Due {task.due_date} · {relativeDue(task.due_date)}
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#5C6B64] mt-1 shrink-0" />
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

function TaskExecutionDialog({ task, asset, template, isVerifier, onClose, onSaved }) {
  const { getHeaders, API } = useAuth();
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({});   // key → {result, value, remark}
  const [remarks, setRemarks] = useState('');
  const [cost, setCost] = useState('');
  const [duration, setDuration] = useState('');
  const [vendor, setVendor] = useState('');
  const [beforePhoto, setBeforePhoto] = useState('');
  const [afterPhoto, setAfterPhoto] = useState('');

  useEffect(() => {
    if (task) {
      setResults({}); setRemarks('');
      setCost(''); setDuration(''); setVendor('');
      setBeforePhoto(''); setAfterPhoto('');
    }
  }, [task]);

  if (!task) return null;

  const setResult = (k, patch) =>
    setResults((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));

  const call = async (op, body) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/maintenance/tasks/${task.id}/${op}`, body || {},
                        { headers: getHeaders() });
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${op} task`);
      return false;
    } finally { setBusy(false); }
  };

  const start = async () => {
    if (await call('start')) { toast.success('Task started'); onSaved(); }
  };

  const complete = async () => {
    // 1. Submit inspection run if template present.
    if (template && asset) {
      const items = (template.items || []).map((it) => ({
        key: it.key,
        result: results[it.key]?.result || 'na',
        value: results[it.key]?.value ? Number(results[it.key].value) : undefined,
        remark: results[it.key]?.remark || '',
      }));
      // A critical item that's not passed blocks completion.
      const criticalUnpassed = (template.items || []).some(
        (it) => it.is_critical && (results[it.key]?.result !== 'pass'),
      );
      if (criticalUnpassed) {
        toast.error('Critical checks must all be Pass before completing');
        return;
      }
      try {
        await axios.post(`${API}/admin/maintenance/assets/${asset.id}/inspections`,
                          { template_id: template.id, items, remarks },
                          { headers: getHeaders() });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Inspection submit failed');
        return;
      }
    }
    // 2. Mark task complete with photos + remarks.
    const ok = await call('complete', {
      remarks, cost: Number(cost) || 0,
      duration_minutes: duration ? Number(duration) : null,
      vendor: vendor || '',
      before_photo: beforePhoto || null,
      after_photo: afterPhoto || null,
    });
    if (ok) { toast.success('Task completed — awaiting verification'); onSaved(); }
  };

  const verify = async () => {
    if (await call('verify', { remarks: remarks || 'Verified from queue' })) {
      toast.success('Verified — service history updated');
      onSaved();
    }
  };

  const status = STATUS_STYLES[task.status] || STATUS_STYLES.pending;

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
                      data-testid="task-exec-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A2421] flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[color:var(--color-primary)]" />
            <span className="font-mono text-sm text-[color:var(--color-primary)]">
              {asset?.asset_tag || 'Task'}
            </span>
            <Badge className={`text-[10px] border ${status.cls}`}>{status.label}</Badge>
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {asset?.name} · {task.description} · Due {task.due_date}
          </DialogDescription>
        </DialogHeader>

        {template && task.status !== 'completed' && (
          <div className="space-y-2" data-testid="exec-inspection-items">
            <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
              Inspection · {template.name}
            </div>
            {(template.items || []).map((it) => (
              <div key={it.key}
                   className="grid grid-cols-[1fr_120px_140px_1fr] gap-2 items-center">
                <div className="text-sm text-[#1A2421]">
                  {it.label}
                  {it.is_critical && (
                    <span className="ml-1 text-[10px] text-[#C45A41] font-semibold">CRITICAL</span>
                  )}
                  {it.unit && (
                    <span className="ml-1 text-[11px] text-[#5C6B64]">
                      ({it.unit}, {it.min_value}–{it.max_value})
                    </span>
                  )}
                </div>
                <Select
                  value={results[it.key]?.result || ''}
                  onValueChange={(v) => setResult(it.key, { result: v })}
                >
                  <SelectTrigger className="h-9 border-[#E2E8DF]"
                                  data-testid={`exec-result-${it.key}`}>
                    <SelectValue placeholder="Result" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="fail">Fail</SelectItem>
                    <SelectItem value="na">N/A</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type={it.kind === 'pass_fail' ? 'text' : 'number'}
                  placeholder={it.kind === 'pass_fail' ? 'n/a' : 'Reading'}
                  disabled={it.kind === 'pass_fail'}
                  value={results[it.key]?.value || ''}
                  onChange={(e) => setResult(it.key, { value: e.target.value })}
                  className="h-9 border-[#E2E8DF]"
                />
                <Input
                  value={results[it.key]?.remark || ''}
                  placeholder="Remark"
                  onChange={(e) => setResult(it.key, { remark: e.target.value })}
                  className="h-9 border-[#E2E8DF]"
                />
              </div>
            ))}
          </div>
        )}

        {task.status !== 'completed' && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="col-span-2 space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
                Remarks
              </Label>
              <Textarea rows={2} value={remarks}
                         onChange={(e) => setRemarks(e.target.value)}
                         placeholder="Work done, parts used, follow-up needed…"
                         className="border-[#E2E8DF]"
                         data-testid="exec-remarks-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Cost (₹)</Label>
              <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)}
                      className="h-10 border-[#E2E8DF]"
                      data-testid="exec-cost-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Duration (min)</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)}
                      className="h-10 border-[#E2E8DF]" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Vendor</Label>
              <Input value={vendor} onChange={(e) => setVendor(e.target.value)}
                      className="h-10 border-[#E2E8DF]" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Before Photo URL</Label>
              <Input value={beforePhoto} onChange={(e) => setBeforePhoto(e.target.value)}
                      placeholder="https://…" className="h-10 border-[#E2E8DF]"
                      data-testid="exec-before-photo" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">After Photo URL</Label>
              <Input value={afterPhoto} onChange={(e) => setAfterPhoto(e.target.value)}
                      placeholder="https://…" className="h-10 border-[#E2E8DF]"
                      data-testid="exec-after-photo" />
            </div>
          </div>
        )}

        {task.status === 'completed' && (
          <div className="text-sm text-[#1A2421] space-y-2 pt-2">
            <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
              Completion summary
            </div>
            <div className="text-xs text-[#5C6B64]">
              Completed by <span className="text-[#1A2421]">{task.completed_by || '—'}</span>
              {' · '} {task.completed_at?.slice(0, 10)}
            </div>
            {task.completion?.remarks && (
              <div className="text-sm text-[#1A2421] whitespace-pre-wrap">
                {task.completion.remarks}
              </div>
            )}
            {(task.completion?.cost > 0 || task.completion?.vendor) && (
              <div className="text-xs text-[#5C6B64]">
                {task.completion?.vendor && <>Vendor: {task.completion.vendor} · </>}
                {task.completion?.cost > 0 && <>Cost: ₹{Number(task.completion.cost).toLocaleString('en-IN')}</>}
              </div>
            )}
            {(task.completion?.before_photo || task.completion?.after_photo) && (
              <div className="flex gap-2">
                {task.completion?.before_photo && (
                  <a href={task.completion.before_photo} target="_blank" rel="noreferrer">
                    <img src={task.completion.before_photo} alt="before"
                         className="w-20 h-20 object-cover rounded border border-[#E2E8DF]" />
                  </a>
                )}
                {task.completion?.after_photo && (
                  <a href={task.completion.after_photo} target="_blank" rel="noreferrer">
                    <img src={task.completion.after_photo} alt="after"
                         className="w-20 h-20 object-cover rounded border border-[#E2E8DF]" />
                  </a>
                )}
              </div>
            )}
            {isVerifier && (
              <div className="space-y-1 pt-2">
                <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
                  Verification remarks (optional)
                </Label>
                <Textarea rows={2} value={remarks}
                           onChange={(e) => setRemarks(e.target.value)}
                           className="border-[#E2E8DF]" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}
                  className="border-[#E2E8DF]">Close</Button>
          {task.status === 'pending' && (
            <Button onClick={start} disabled={busy}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="exec-start-btn">
              <PlayCircle className="w-3.5 h-3.5 mr-1" />
              Start task
            </Button>
          )}
          {task.status === 'in_progress' && (
            <Button onClick={complete} disabled={busy}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="exec-complete-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Mark complete
            </Button>
          )}
          {task.status === 'completed' && isVerifier && (
            <Button onClick={verify} disabled={busy}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="exec-verify-btn">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" />
              Verify & close
            </Button>
          )}
          {task.status === 'completed' && !isVerifier && (
            <div className="text-xs text-[#5C6B64] italic pr-2">
              Awaiting verification by a manager.
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
